import { building_state_enum } from '@predium/enums';
import { PropsWithChildren, createContext, useState } from 'react';
import { ALL_ECONOMIC_UNITS } from '../sections/StatusQuoAnalysis/NavigationSelectors/EconomicUnitsSelector';
import { ALL_PORTFOLIOS } from '../sections/StatusQuoAnalysis/NavigationSelectors/PortfolioSelector';

type DataCollectionFilters = {
  selectedPortfolios: string[];
  selectedStatus: building_state_enum | 'all';
  filterName: string;
};

type AllDataCollectionFilters = {
  dataCollectionBuildingFilters: DataCollectionFilters;
  dataCollectionDraftsFilters: Omit<DataCollectionFilters, 'selectedStatus'> & {
    selectedEconomicUnits: string[];
    selectedResponsibleUserIds: number[];
  };
  dataCollectionEconomicUnitsFilters: Omit<DataCollectionFilters, 'selectedStatus'>;
};

type DataCollectionFiltersContextProps = {
  filters: AllDataCollectionFilters;
  setSelectedPortfolios: (
    filterType: 'building' | 'drafts' | 'economicUnits',
    selectedValues: string[] | ((prev: string[]) => string[]),
  ) => void;
  setSelectedStatusBuilding: (selectedStatus: building_state_enum | 'all') => void;
  setFilterNameBuilding: (name: string) => void;
  setFilterNameEconomicUnit: (economicUnit: string) => void;
  resetAllFilters: () => void;
  setFilterAddressDrafts: (address: string) => void;
  setSelectedEconomicUnitsDrafts: (selectedEconomicUnits: string[]) => void;
  setSelectedResponsibleUsersDrafts: (ids: number[]) => void;
};

const initialFilters: AllDataCollectionFilters = {
  dataCollectionBuildingFilters: {
    selectedPortfolios: [ALL_PORTFOLIOS],
    selectedStatus: 'all',
    filterName: '',
  },
  dataCollectionDraftsFilters: {
    selectedPortfolios: [ALL_PORTFOLIOS],
    selectedEconomicUnits: [ALL_ECONOMIC_UNITS],
    selectedResponsibleUserIds: [],
    filterName: '',
  },
  dataCollectionEconomicUnitsFilters: {
    selectedPortfolios: [ALL_PORTFOLIOS],
    filterName: '',
  },
};

const DataCollectionFiltersContext = createContext<DataCollectionFiltersContextProps | null>(null);

function DataCollectionFilterContextProvider({ children }: PropsWithChildren) {
  const [filters, setFilters] = useState<AllDataCollectionFilters>(initialFilters);
  const filterKeyMap = {
    building: 'dataCollectionBuildingFilters',
    drafts: 'dataCollectionDraftsFilters',
    economicUnits: 'dataCollectionEconomicUnitsFilters',
  } as const;

  // TODO PRE-2034
  const resetAllFilters = () => {
    setFilters(initialFilters);
  };

  const setSelectedPortfolios = (
    filterType: keyof typeof filterKeyMap,
    selectedValues: string[] | ((prev: string[]) => string[]),
  ) => {
    setFilters((prevFilters) => {
      const key = filterKeyMap[filterType];

      const newSelectedPortfolios =
        typeof selectedValues === 'function' ? selectedValues(prevFilters[key].selectedPortfolios) : selectedValues;

      return {
        ...prevFilters,
        [key]: {
          ...prevFilters[key],
          selectedPortfolios: newSelectedPortfolios,
        },
      };
    });
  };

  const setFilterAddressDrafts = (address: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dataCollectionDraftsFilters: {
        ...prevFilters.dataCollectionDraftsFilters,
        filterName: address,
      },
    }));
  };

  const setSelectedEconomicUnitsDrafts = (selectedEconomicUnits: string[]) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dataCollectionDraftsFilters: {
        ...prevFilters.dataCollectionDraftsFilters,
        selectedEconomicUnits,
      },
    }));
  };

  const setSelectedResponsibleUsersDrafts = (ids: number[]) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dataCollectionDraftsFilters: {
        ...prevFilters.dataCollectionDraftsFilters,
        selectedResponsibleUserIds: ids,
      },
    }));
  };

  const setSelectedStatusBuilding = (selectedStatus: building_state_enum | 'all') => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dataCollectionBuildingFilters: {
        ...prevFilters.dataCollectionBuildingFilters,
        selectedStatus,
      },
    }));
  };

  const setFilterNameBuilding = (filterName: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dataCollectionBuildingFilters: {
        ...prevFilters.dataCollectionBuildingFilters,
        filterName,
      },
    }));
  };

  const setFilterNameEconomicUnit = (filterName: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dataCollectionEconomicUnitsFilters: {
        ...prevFilters.dataCollectionEconomicUnitsFilters,
        filterName,
      },
    }));
  };

  return (
    <DataCollectionFiltersContext.Provider
      value={{
        filters,
        setSelectedStatusBuilding,
        setFilterNameBuilding,
        setFilterNameEconomicUnit,
        setSelectedPortfolios,
        resetAllFilters,
        setFilterAddressDrafts,
        setSelectedEconomicUnitsDrafts,
        setSelectedResponsibleUsersDrafts,
      }}
    >
      {children}
    </DataCollectionFiltersContext.Provider>
  );
}

export { DataCollectionFilterContextProvider, DataCollectionFiltersContext };
